import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AuthTokenRES,
  BffUserRES,
  FactorsRES,
  FirmRES,
  GetTenantRES,
  Masquerading
} from '@smarsh-cyber/shared/api';
import {
  AppInit,
  AuthToken,
  ErrorModel,
  FactorsModel
} from '@smarsh-cyber/shared/models';
import {
  Observable,
  catchError,
  concatMap,
  first,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  throwError
} from 'rxjs';
import { SecureKeyword } from '../../layout/layout/app-main.modal';
import { POSITION_IDS } from '../../shared/constants';
import { DataService } from '../data/data.service';
import {
  translateFactors,
  translateGetAppInitData,
  translateRefreshToken,
  translateUser,
  translateUserPermission
} from '../utils/app-state.translator';
import { appVersion } from 'src/environments/version';

@Injectable({
  providedIn: 'root'
})
export class UIService {
  constructor(private dataService: DataService) {}

  private fetchTenantDetails(portalName: string, token: AuthTokenRES) {
    return this.dataService
      .getTenantDetails(portalName, token)
      .pipe(map((tenantDetails) => ({ tenantDetails, token })));
  }

  private fetchOrgAndBffDetails(tenantId: string, token: AuthTokenRES) {
    return forkJoin({
      orgList: this.dataService.getActiveFirms(tenantId, token),
      bffUserDetails: this.dataService.getBffUser(tenantId, token)
    });
  }

  private fetchUserPermissions(
    token: AuthTokenRES,
    tenantDetails: GetTenantRES,
    orgList: FirmRES,
    bffUserDetails: BffUserRES
  ) {
    return this.dataService
      .getUserPermissionsPreAppInit(
        token,
        tenantDetails?.data?.tenantId,
        bffUserDetails.userId,
        bffUserDetails.buId
      )
      .pipe(
        map((permissions) =>
          translateGetAppInitData(
            token,
            orgList,
            tenantDetails,
            bffUserDetails,
            permissions.data,
            null,
            null
          )
        )
      );
  }

  // TODO: check subscription call, temporarily needed, need to remove post feature flag
  private checkSubscription(
    token: AuthTokenRES,
    tenantDetails: GetTenantRES,
    orgList: FirmRES,
    bffUserDetails: BffUserRES,
    portalURL: string,
    portalName: string,
    masquerading: Masquerading
  ) {
    const userData = bffUserDetails;
    const shouldCheckSubscription = [...SecureKeyword].includes(portalName);
    const isSO = userData?.positionId == POSITION_IDS.SO;
    const hasApplianceId = !!userData?.applianceId;

    // For SO users, return dummy response if subscription check is needed
    const dummyResponse = { serviceStatus: 'TRUE' };

    const getSubscriptionData = (serviceType: 'SAT' | 'PHISHING') => {
      if (!shouldCheckSubscription) return of(null);
      if (isSO) return of(dummyResponse);
      if (!hasApplianceId) return of(null);
      return this.dataService.checkSubscription(
        portalURL,
        userData.applianceId,
        serviceType
      );
    };

    return forkJoin({
      sat: getSubscriptionData('SAT'),
      phishing: getSubscriptionData('PHISHING')
    }).pipe(
      map(({ sat, phishing }) =>
        translateGetAppInitData(
          token,
          orgList,
          tenantDetails,
          bffUserDetails,
          null,
          { sat, phishing },
          masquerading
        )
      )
    );
  }

  getAppInitData(
    portalName: string,
    portalUrl: string,
    code?: string,
    refreshToken?: string
  ): Observable<AppInit> {
    // no code or refresh token
    if (!code && !refreshToken) {
      return this.dataService.getUserDetail(portalUrl).pipe(
        map((userDetails) => {
          return userDetails;
        }),
        switchMap((userDetails) =>
          this.getTenantAndSubscriptionData(
            portalName,
            userDetails.java_auth_token_data,
            portalUrl,
            userDetails.masquerading
          )
        )
      );
    } else {
      // either code or refreshToken
      return (
        code
          ? this.dataService.getAuthTokenFromCode(code, portalName)
          : this.dataService.refreshAuthToken(refreshToken!, portalName)
      ).pipe(
        map((res) => res),
        switchMap((token: AuthTokenRES) =>
          this.getTenantAndSubscriptionData(portalName, token, portalUrl, null)
        )
      );
    }
  }

  getTenantAndSubscriptionData(
    portalName: string,
    token: AuthTokenRES,
    portalUrl: string,
    masquerading: Masquerading
  ) {
    return this.dataService.getTenantDetails(portalName, token).pipe(
      mergeMap((tenantDetails) =>
        this.fetchOrgAndBffDetails(tenantDetails?.data.tenantId, token).pipe(
          map(({ orgList, bffUserDetails }) => ({
            orgList,
            bffUserDetails,
            tenantDetails,
            token
          }))
        )
      ),
      concatMap(({ orgList, bffUserDetails, tenantDetails, token }) =>
        // this.fetchUserPermissions(
        //   token,
        //   tenantDetails,
        //   orgList,
        //   bffUserDetails.data[0] || null
        // )
        this.checkSubscription(
          token,
          tenantDetails,
          orgList,
          bffUserDetails.data[0] || null,
          portalUrl,
          portalName,
          masquerading
        )
      ),
      catchError((error: HttpErrorResponse) =>
        throwError(
          () => ({ message: error.message, status: error.status }) as ErrorModel
        )
      )
    );
  }

  refreshAuthToken(
    refreshToken: string,
    portalName: string
  ): Observable<AuthToken> {
    return this.dataService.refreshAuthToken(refreshToken, portalName).pipe(
      map((res: AuthTokenRES) => {
        return translateRefreshToken(res);
      })
    );
  }

  getFactors(): Observable<FactorsModel> {
    return this.dataService.getFactors().pipe(
      map((res: FactorsRES) => {
        return translateFactors(res);
      })
    );
  }

  getUserPermissionsPreAppInit(
    token: AuthTokenRES,
    tenantId: string,
    userId: string,
    buId: string | null
  ) {
    return this.dataService
      .getUserPermissionsPreAppInit(token, tenantId, userId, buId)
      .pipe(
        map((response) => {
          return translateUserPermission(response);
        }),
        catchError((err: HttpErrorResponse) => {
          return of(translateUserPermission(err));
        })
      );
  }

  getUserDetailsWithPermissions(user: BffUserRES, portalURL: string) {
    return forkJoin({
      sat: this.dataService.checkSubscription(
        portalURL,
        user?.applianceId,
        'SAT'
      ),
      phishing: this.dataService.checkSubscription(
        portalURL,
        user?.applianceId,
        'PHISHING'
      )
    }).pipe(
      map(({ sat, phishing }) => translateUser(user, [], { sat, phishing }))
    );

    // return this.dataService.getUserPermissions(user.userId, user.buId).pipe(
    //   map((response) => {
    //     return translateUser(user, response.data, {
    //       //NOTE temp solution for serviceStatus check in translate user -> subscriptions
    //       sat: tempArgLoggedInUserPermissons.includes(
    //         FEATURE_ACCESS_MAP.SAT_READ
    //       )
    //         ? serviceStatus
    //         : null,
    //       phishing: tempArgLoggedInUserPermissons.includes(
    //         FEATURE_ACCESS_MAP.PHISHING_READ
    //       )
    //         ? serviceStatus
    //         : null
    //     });
    //   })
    //   //TODO handle error
    //   // catchError((err: HttpErrorResponse) => {
    //   //   return of(translateUserPermission(err));
    //   // })
    // );
  }

  isNewAppVersionAvailable(): Observable<boolean> {
    return this.dataService.getLatestAppVersion().pipe(
      first(),
      map((res) => {
        const latest = res?.version?.trim?.();
        const current = appVersion?.trim?.();
        return !!latest && !!current && current !== latest;
      }),
      catchError(() => {
        return of(false); // treat as up-to-date to avoid blocking the app
      })
    );
  }
}
